import React, { ReactNode } from 'react';

import { useTheme } from '../../contexts/ThemeContext';
import { ThemeToggle } from '../ui/ThemeToggle';

interface MobileHeaderProps {
  title?: string;
  subtitle?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  onLeftClick?: () => void;
  onRightClick?: () => void;
  variant?: 'default' | 'transparent' | 'gradient';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  children?: ReactNode;
  showThemeToggle?: boolean;
}

export const MobileHeader: React.FC<MobileHeaderProps> = ({
  title,
  subtitle,
  leftIcon,
  rightIcon,
  onLeftClick,
  onRightClick,
  variant = 'default',
  size = 'md',
  className = '',
  children,
  showThemeToggle = false,
}) => {
  const { isDark } = useTheme();

  // Get variant classes
  const getVariantClasses = () => {
    switch (variant) {
      case 'transparent':
        return 'bg-transparent';
      case 'gradient':
        return 'bg-gradient-to-r from-blue-600 to-purple-600 text-white';
      default:
        return 'bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800';
    }
  };

  // Get size classes
  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'px-4 py-2 min-h-[56px]';
      case 'lg':
        return 'px-6 py-4 min-h-[72px]';
      default:
        return 'px-4 py-3 min-h-[64px]';
    }
  };

  return (
    <header
      className={`
      ${getVariantClasses()}
      ${getSizeClasses()}
      flex items-center justify-between
      ${className}
    `}
    >
      {/* Left Section */}
      <div className='flex items-center gap-3'>
        {/* Left Action Button */}
        {(leftIcon || onLeftClick) && (
          <button
            onClick={onLeftClick}
            className={`
              p-2 rounded-xl transition-all duration-300 flex items-center justify-center
              ${
                variant === 'gradient'
                  ? 'hover:bg-white/20 active:bg-white/30 text-white'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-800 active:bg-gray-200 dark:active:bg-gray-700 text-gray-700 dark:text-gray-300'
              }
            `}
            aria-label='الرجوع'
          >
            {leftIcon || (
              <svg className='w-5 h-5' fill='currentColor' viewBox='0 0 20 20'>
                <path
                  fillRule='evenodd'
                  d='M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z'
                  clipRule='evenodd'
                />
              </svg>
            )}
          </button>
        )}

        {/* Title Section */}
        {(title || subtitle) && (
          <div className='flex-1 min-w-0'>
            {title && (
              <h1
                className={`
                font-bold truncate
                ${
                  size === 'lg'
                    ? 'text-xl'
                    : size === 'sm'
                    ? 'text-base'
                    : 'text-lg'
                }
                ${
                  variant === 'gradient'
                    ? 'text-white'
                    : 'text-gray-900 dark:text-gray-100'
                }
              `}
              >
                {title}
              </h1>
            )}

            {subtitle && (
              <p
                className={`
                text-sm truncate mt-1
                ${
                  variant === 'gradient'
                    ? 'text-blue-100'
                    : 'text-gray-500 dark:text-gray-400'
                }
              `}
              >
                {subtitle}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Center Content (Custom children) */}
      {children && (
        <div className='flex-1 flex items-center justify-center mx-4'>
          {children}
        </div>
      )}

      {/* Right Section */}
      <div className='flex items-center gap-2'>
        {showThemeToggle && (
          <ThemeToggle
            variant='minimal'
            size={size === 'lg' ? 'lg' : size === 'sm' ? 'sm' : 'md'}
          />
        )}

        {(rightIcon || onRightClick) && (
          <button
            onClick={onRightClick}
            className={`
              p-2 rounded-xl transition-all duration-300 flex items-center justify-center
              ${
                variant === 'gradient'
                  ? 'hover:bg-white/20 active:bg-white/30 text-white'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-800 active:bg-gray-200 dark:active:bg-gray-700 text-gray-700 dark:text-gray-300'
              }
            `}
            aria-label='المزيد'
          >
            {rightIcon || (
              <svg className='w-5 h-5' fill='currentColor' viewBox='0 0 20 20'>
                <path d='M10 6a2 2 0 110-4 2 2 0 110 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z' />
              </svg>
            )}
          </button>
        )}
      </div>
    </header>
  );
};

// Page Header Component with more features
interface MobilePageHeaderProps {
  title: string;
  subtitle?: string;
  description?: string;
  avatar?: ReactNode;
  badge?: ReactNode;
  actions?: ReactNode;
  tabs?: {
    id: string;
    label: string;
    active?: boolean;
    onClick?: () => void;
  }[];
  onBack?: () => void;
  variant?: 'default' | 'centered' | 'minimal';
  className?: string;
}

export const MobilePageHeader: React.FC<MobilePageHeaderProps> = ({
  title,
  subtitle,
  description,
  avatar,
  badge,
  actions,
  tabs,
  onBack,
  variant = 'default',
  className = '',
}) => {
  const { isDark } = useTheme();

  return (
    <div className={`bg-white dark:bg-gray-900 ${className}`}>
      {/* Main Header */}
      <div className='px-4 py-4'>
        <div className='flex items-start gap-4'>
          {/* Back Button */}
          {onBack && (
            <button
              onClick={onBack}
              className='p-2 -mr-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex-shrink-0 mt-1'
              aria-label='الرجوع'
            >
              <svg
                className='w-5 h-5 text-gray-700 dark:text-gray-300'
                fill='currentColor'
                viewBox='0 0 20 20'
              >
                <path
                  fillRule='evenodd'
                  d='M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z'
                  clipRule='evenodd'
                />
              </svg>
            </button>
          )}

          {/* Avatar */}
          {avatar && <div className='flex-shrink-0'>{avatar}</div>}

          {/* Content */}
          <div className='flex-1 min-w-0'>
            <div
              className={`
              ${variant === 'centered' ? 'text-center' : 'text-right'}
            `}
            >
              {/* Title & Badge */}
              <div className='flex items-center justify-between mb-2'>
                <h1 className='text-xl font-bold text-gray-900 dark:text-gray-100 truncate'>
                  {title}
                </h1>
                {badge && <div className='mr-3'>{badge}</div>}
              </div>

              {/* Subtitle */}
              {subtitle && (
                <p className='text-gray-500 dark:text-gray-400 text-sm mb-2 truncate'>
                  {subtitle}
                </p>
              )}

              {/* Description */}
              {description && (
                <p className='text-gray-600 dark:text-gray-300 text-sm leading-relaxed'>
                  {description}
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          {actions && <div className='flex-shrink-0'>{actions}</div>}
        </div>
      </div>

      {/* Tabs */}
      {tabs && tabs.length > 0 && (
        <div className='border-t border-gray-200 dark:border-gray-800'>
          <div className='flex overflow-x-auto scrollbar-hide'>
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={tab.onClick}
                className={`
                  flex-shrink-0 px-4 py-3 text-sm font-medium transition-all duration-300 border-b-2 whitespace-nowrap
                  ${
                    tab.active
                      ? 'text-blue-600 dark:text-blue-400 border-blue-600 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                      : 'text-gray-600 dark:text-gray-400 border-transparent hover:text-gray-900 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Search Header Component
interface MobileSearchHeaderProps {
  value?: string;
  placeholder?: string;
  onSearch?: (query: string) => void;
  onClear?: () => void;
  onBack?: () => void;
  suggestions?: string[];
  loading?: boolean;
  className?: string;
}

export const MobileSearchHeader: React.FC<MobileSearchHeaderProps> = ({
  value = '',
  placeholder = 'البحث...',
  onSearch,
  onClear,
  onBack,
  suggestions = [],
  loading = false,
  className = '',
}) => {
  const [query, setQuery] = React.useState(value);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    setQuery(value);
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    onSearch?.(newQuery);
    setShowSuggestions(newQuery.length > 0 && suggestions.length > 0);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    onSearch?.(suggestion);
    setShowSuggestions(false);
  };

  const handleClear = () => {
    setQuery('');
    onSearch?.('');
    onClear?.();
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  return (
    <div className={`bg-white dark:bg-gray-900 ${className}`}>
      {/* Search Input */}
      <div className='p-4'>
        <div className='flex items-center gap-3'>
          {/* Back Button */}
          {onBack && (
            <button
              onClick={onBack}
              className='p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex-shrink-0'
              aria-label='الرجوع'
            >
              <svg
                className='w-5 h-5 text-gray-700 dark:text-gray-300'
                fill='currentColor'
                viewBox='0 0 20 20'
              >
                <path
                  fillRule='evenodd'
                  d='M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z'
                  clipRule='evenodd'
                />
              </svg>
            </button>
          )}

          {/* Search Input */}
          <div className='relative flex-1'>
            <input
              ref={inputRef}
              type='text'
              value={query}
              onChange={handleInputChange}
              onFocus={() =>
                setShowSuggestions(query.length > 0 && suggestions.length > 0)
              }
              placeholder={placeholder}
              className='w-full px-4 py-3 pr-12 bg-gray-100 dark:bg-gray-800 border border-transparent rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-gray-700 transition-all duration-300'
            />

            {/* Search Icon or Loading */}
            <div className='absolute left-4 top-1/2 transform -translate-y-1/2'>
              {loading ? (
                <div className='w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin'></div>
              ) : (
                <svg
                  className='w-4 h-4 text-gray-500 dark:text-gray-400'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='m21 21-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                  />
                </svg>
              )}
            </div>

            {/* Clear Button */}
            {query && (
              <button
                onClick={handleClear}
                className='absolute left-10 top-1/2 transform -translate-y-1/2 p-1 rounded-full hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors'
                aria-label='مسح البحث'
              >
                <svg
                  className='w-3 h-3 text-gray-500 dark:text-gray-400'
                  fill='currentColor'
                  viewBox='0 0 20 20'
                >
                  <path
                    fillRule='evenodd'
                    d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
                    clipRule='evenodd'
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Suggestions */}
      {showSuggestions && (
        <div className='border-t border-gray-200 dark:border-gray-800'>
          <div className='max-h-60 overflow-y-auto'>
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                className='w-full px-4 py-3 text-right text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center gap-3'
              >
                <svg
                  className='w-4 h-4 text-gray-400 dark:text-gray-500'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='m21 21-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                  />
                </svg>
                <span className='truncate'>{suggestion}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default { MobileHeader, MobilePageHeader, MobileSearchHeader };
